import BoltIcon from "@mui/icons-material/Bolt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import DeselectIcon from "@mui/icons-material/Deselect";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Alert, Snackbar } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Route as RouteIcon } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router";

import { useNavigation } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { useFileTransfer } from "@/providers/FileTransfer";
import { useCreateExportJobMutation } from "@/shared/domain/exportJobs/hooks/useCreateExportJobMutation";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { FileRecord } from "@/shared/domain/files";
import {
  DirectoryNode,
  FileSystemNode,
  isFile,
  downloadFile,
  selectedItemsToFileIds,
  selectedItemsToPatterns,
  shouldUseZipExport,
} from "@/shared/domain/files/filesystem";
import { OrgRecord } from "@/shared/domain/orgs";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { pluralize } from "@/shared/text";
import { FileSignedUrlResponse, fileSignedUrlEndpoint } from "@/types";

import { useFileRecords } from "../domain/files/hooks/useFile";

import { AlertDialog } from "./AlertDialog";
import { RobotoButton } from "./RobotoButton";
import { RobotoStyledPopoverMenu } from "./RobotoStyledPopoverMenu";
import styles from "./SelectionButton.module.css";
import { SessionsSubmenu } from "./sessions";

interface SelectionButtonProps {
  datasetId?: string;
  selectedItems?: FileSystemNode[];
  selectedDatasets?: Set<string>;
  setSelectedDatasets?: (arg: Set<string>) => void;
  currentOrganization: OrgRecord | null;
  handleCollectionPopoverOpen?: (
    event: React.MouseEvent<HTMLElement>,
    overrideAnchorEl?: HTMLElement | null,
  ) => void;
  handleRemoveFromCollection?: () => void;
  handleDeleteItems?: () => void;
  enableDeleteDatasets?: boolean;
  clearSelected?: () => void;
  handleDeselectAll?: () => void;
}

export const SelectionButton: React.FC<SelectionButtonProps> = ({
  datasetId,
  selectedItems,
  selectedDatasets,
  setSelectedDatasets,
  currentOrganization,
  handleCollectionPopoverOpen,
  handleDeselectAll,
  handleRemoveFromCollection,
  handleDeleteItems,
  enableDeleteDatasets = false,
  clearSelected,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // The Sessions submenu anchors to the main menu's Paper element so it matches
  // its height (top and bottom edges align) and cascades flush to its right.
  const [menuPaperEl, setMenuPaperEl] = React.useState<HTMLElement | null>(
    null,
  );
  const [sessionsSubmenuOpen, setSessionsSubmenuOpen] =
    React.useState<boolean>(false);

  const navigate = useNavigate();
  const { goto } = useNavigation();
  const { datasetService, files: fileService } = useDomainServices();
  const { beginExportJob } = useFileTransfer();
  const sessionsEnabled = useCheckFeatureFlag("release-sessions");

  const fileIds = selectedItemsToFileIds(selectedItems ?? []);

  const { fetchFileRecords } = useFileRecords(fileIds);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSessionsSubmenuOpen(false);
  };

  const [alertDialogOpen, setAlertDialogOpen] = React.useState<boolean>(false);
  const [alertDialogTitle, setAlertDialogTitle] = React.useState<string>("");
  const [alertDialogText, setAlertDialogText] = React.useState<string>("");
  const [alertDialogContent, setAlertDialogContent] =
    React.useState<React.ReactNode>();
  const [alertDialogAction, setAlertDialogAction] =
    React.useState<() => Promise<void>>();

  const errorInfo = (errorText: string) => {
    return (
      <div className={styles["error-info"]}>
        <Alert severity="error">{errorText}</Alert>
      </div>
    );
  };

  const createExportJob = useCreateExportJobMutation();
  const [exportError, setExportError] = React.useState<string | null>(null);

  // ZIP export when >3 files or any directory is selected
  const useZipExport = React.useMemo(
    () => shouldUseZipExport(selectedItems ?? []),
    [selectedItems],
  );

  const { initiateRequest: initiateSignedUrlRequest } =
    useLazyAPICall<FileSignedUrlResponse>();

  const fetchSignedUrl = async (file: FileRecord) => {
    if (!currentOrganization) {
      return;
    }

    const { data, error } = await initiateSignedUrlRequest({
      endpoint: fileSignedUrlEndpoint,
      method: "GET",
      orgId: currentOrganization?.org_id,
      queryParams: fileService.getSignedUrlParams(file, true),
      pathParams: { fileId: file.file_id },
    });

    if (!error && data?.data) {
      return data.data.url;
    }
  };

  const selectedItemsCount = React.useMemo(() => {
    return (selectedItems && selectedItems.length) || 0;
  }, [selectedItems]);

  const selectedDatasetsCount = React.useMemo(() => {
    return (selectedDatasets && selectedDatasets.size) || 0;
  }, [selectedDatasets]);

  const totalSelected = React.useMemo(() => {
    return selectedItemsCount + selectedDatasetsCount;
  }, [selectedDatasetsCount, selectedItemsCount]);

  const onClick = async () => {
    if (!selectedItems) {
      return;
    }

    try {
      const fileRecords = await fetchFileRecords();

      for (const item of fileRecords) {
        const signedUrl = await fetchSignedUrl(item);

        if (signedUrl) {
          downloadFile(item.name, signedUrl);
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
      }
    } catch (error) {
      ErrorMonitoringService.captureError(error);
      setAlertDialogContent(
        errorInfo("Failed to download file(s). Please try again."),
      );
    }
    handleClose();
  };

  const deleteDatasets = async () => {
    if (!selectedDatasets || !currentOrganization) {
      return;
    }

    const selectedDatasetsArr = Array.from(selectedDatasets);

    for (const datasetId of selectedDatasetsArr) {
      try {
        await datasetService.deleteDataset(datasetId, {
          resourceOwnerId: currentOrganization.org_id,
        });
      } catch (error) {
        ErrorMonitoringService.captureError(error);
        setAlertDialogContent(
          errorInfo("Failed to delete dataset(s). Please try again later."),
        );
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // All deletions finished without issue
    setAlertDialogOpen(false);
    void navigate(0);
  };

  const handleDeleteDatasets = () => {
    if (!selectedDatasets) {
      return;
    }

    setAlertDialogContent(null);
    setAlertDialogTitle(
      `Delete ${pluralize("Dataset", selectedDatasets.size)}`,
    );
    setAlertDialogText(
      `Are you sure you want to delete ${selectedDatasets.size} ${pluralize("dataset", selectedDatasets.size)}?`,
    );
    setAlertDialogAction(() => deleteDatasets);

    // Close the selection popover and show the confirmation dialog
    handleClose();
    setAlertDialogOpen(true);
  };

  return (
    <>
      <RobotoButton
        id="selection-button"
        aria-controls={open ? "selection-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        eventName={"AddToCollectionOptionSelected"}
        sx={{
          width: "120px",
          minWidth: "120px",
          height: "32px",
          whiteSpace: "nowrap",
        }}
        disabled={totalSelected < 1}
      >
        Selected ({totalSelected})
      </RobotoButton>
      <RobotoStyledPopoverMenu
        id="selection-menu"
        MenuListProps={{
          "aria-labelledby": "selection-button",
        }}
        slotProps={{
          paper: {
            ref: setMenuPaperEl,
            className: sessionsSubmenuOpen
              ? styles.menuPaperSubmenuOpen
              : undefined,
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          disableRipple
          onClick={() => {
            if (handleDeselectAll) {
              handleDeselectAll();
            }
            if (setSelectedDatasets) {
              setSelectedDatasets(new Set<string>());
            }
            if (clearSelected) {
              clearSelected();
            }
            handleClose();
          }}
        >
          <DeselectIcon />
          Deselect All
        </MenuItem>

        {selectedItemsCount > 0 && handleDeleteItems && (
          <MenuItem
            disableRipple
            disabled={selectedItems && selectedItems.length < 1}
            onClick={() => {
              if (!selectedItems) {
                return;
              }
              if (handleDeleteItems) {
                handleDeleteItems();
              }
              handleClose();
            }}
          >
            <DeleteIcon /> Delete Items
          </MenuItem>
        )}

        {selectedItemsCount > 0 && (
          <MenuItem
            disableRipple
            disabled={selectedItems && selectedItems.length < 1}
            onClick={() => {
              if (useZipExport) {
                if (!selectedItems) {
                  return;
                }
                const nodeIds = selectedItems.map((item) =>
                  isFile(item)
                    ? item.file.file_id
                    : (item as DirectoryNode).directory.directory_id,
                );
                let label: string;
                if (selectedItems.length === 1) {
                  const single = selectedItems[0];
                  label = isFile(single)
                    ? single.file.name
                    : (single as DirectoryNode).directory.name;
                } else {
                  label = `${selectedItems.length} items`;
                }
                createExportJob
                  .mutateAsync({ nodeIds })
                  .then((result) => {
                    beginExportJob(result.export_job_id, label);
                  })
                  .catch((error: unknown) => {
                    ErrorMonitoringService.captureError(error);
                    setExportError(
                      error instanceof RobotoDomainException
                        ? error.message
                        : "Failed to start ZIP export. Please try again.",
                    );
                  });
              } else {
                void onClick();
              }
              handleClose();
            }}
          >
            <DownloadIcon />{" "}
            {useZipExport ? "Download as ZIP" : "Download Files"}
          </MenuItem>
        )}

        {selectedItems && selectedItemsCount > 0 && (
          <MenuItem
            disableRipple
            disabled={selectedItems.filter(isFile).length === 0}
            onClick={() => {
              const selectedFiles = selectedItems.filter(isFile);
              goto.visualize({
                dataSource: {
                  files: selectedFiles.map((item) => item.file.file_id),
                },
              });
            }}
          >
            <QueryStatsIcon /> Visualize Files
          </MenuItem>
        )}

        {enableDeleteDatasets && selectedDatasetsCount > 0 && (
          <MenuItem
            disableRipple
            disabled={selectedDatasets && selectedDatasets.size < 1}
            onClick={handleDeleteDatasets}
          >
            <DeleteIcon /> Delete Datasets
          </MenuItem>
        )}

        {datasetId && selectedItems && selectedItemsCount > 0 && (
          <MenuItem
            disableRipple
            onClick={() => {
              goto.invokeAction({
                datasetId: datasetId,
                inputFiles: selectedItemsToPatterns(selectedItems),
              });
            }}
          >
            <BoltIcon /> Invoke Action
          </MenuItem>
        )}

        <SessionsSubmenuTrigger
          sessionsEnabled={sessionsEnabled}
          datasetId={datasetId}
          selectedItems={selectedItems}
          submenuOpen={sessionsSubmenuOpen}
          onOpen={() => setSessionsSubmenuOpen(true)}
        />

        {handleCollectionPopoverOpen && (
          <MenuItem
            disableRipple
            disabled={
              !(
                (selectedDatasets && selectedDatasets.size > 0) ||
                (selectedItems && selectedItems.filter(isFile).length > 0)
              )
            }
            onClick={(e) => {
              handleClose();
              handleCollectionPopoverOpen(e, anchorEl);
            }}
          >
            <DatasetOutlinedIcon />
            Add to Collection
          </MenuItem>
        )}

        {handleRemoveFromCollection && (
          <MenuItem
            disableRipple
            disabled={
              !(
                (selectedDatasets && selectedDatasets.size > 0) ||
                (selectedItems && selectedItems.filter(isFile).length > 0)
              )
            }
            onClick={() => {
              handleClose();
              handleRemoveFromCollection();
            }}
          >
            <RemoveCircleOutlineIcon />
            Remove from Collection
          </MenuItem>
        )}
      </RobotoStyledPopoverMenu>
      <GuardedSessionsSubmenu
        sessionsEnabled={sessionsEnabled}
        datasetId={datasetId}
        selectedItems={selectedItems}
        anchorEl={menuPaperEl}
        open={sessionsSubmenuOpen}
        onClose={() => setSessionsSubmenuOpen(false)}
        onCloseMenu={handleClose}
      />
      <AlertDialog
        dialogOpen={alertDialogOpen}
        handleClose={() => {
          setAlertDialogOpen(false);
        }}
        dialogTitle={alertDialogTitle}
        dialogText={alertDialogText}
        dialogContent={alertDialogContent}
        dialogAction={alertDialogAction}
        suppressAutoClose={true}
      />
      <Snackbar
        open={exportError !== null}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      </Snackbar>
    </>
  );
};

interface SessionsSubmenuTriggerProps {
  sessionsEnabled: boolean;
  datasetId: string | undefined;
  selectedItems: FileSystemNode[] | undefined;
  submenuOpen: boolean;
  onOpen: () => void;
}

/**
 * "Sessions" item in the selection menu; opens the sessions fly-out. Session
 * actions need the release-sessions flag, a dataset scope, and a non-empty
 * selection to seed the session, so this renders nothing without all three.
 */
function SessionsSubmenuTrigger({
  sessionsEnabled,
  datasetId,
  selectedItems,
  submenuOpen,
  onOpen,
}: SessionsSubmenuTriggerProps) {
  if (
    !sessionsEnabled ||
    !datasetId ||
    !selectedItems ||
    selectedItems.length === 0
  ) {
    return null;
  }
  return (
    <MenuItem
      id="sessions-submenu-button"
      disableRipple
      aria-haspopup="true"
      aria-controls={submenuOpen ? "sessions-submenu" : undefined}
      aria-expanded={submenuOpen ? "true" : undefined}
      onClick={onOpen}
    >
      <RouteIcon /> Sessions
      <ChevronRightIcon className={styles.submenuChevron} />
    </MenuItem>
  );
}

interface GuardedSessionsSubmenuProps {
  sessionsEnabled: boolean;
  datasetId: string | undefined;
  selectedItems: FileSystemNode[] | undefined;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onCloseMenu: () => void;
}

/**
 * SessionsSubmenu rendered only when session actions are available — the
 * release-sessions flag is on and there is a non-empty dataset-scoped
 * selection, the same condition that shows `SessionsSubmenuTrigger`. Must
 * stay a sibling of the selection menu, not a child of it: a closed MUI Menu
 * unmounts its children, which would kill the fly-out's add-to-session dialog
 * when "Add to existing" closes the menu.
 */
function GuardedSessionsSubmenu({
  sessionsEnabled,
  datasetId,
  selectedItems,
  anchorEl,
  open,
  onClose,
  onCloseMenu,
}: GuardedSessionsSubmenuProps) {
  if (
    !sessionsEnabled ||
    !datasetId ||
    !selectedItems ||
    selectedItems.length === 0
  ) {
    return null;
  }
  return (
    <SessionsSubmenu
      datasetId={datasetId}
      selectedItems={selectedItems}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      onCloseMenu={onCloseMenu}
    />
  );
}
